/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, December 23rd 2021, 10:08:12 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useAPILazyLoadPreBuiltServiceTemplates } from "prebuilt-services/services/PreBuiltServicesAPI";
import { PreBuiltService, ProcessTemplateCategory } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import { PreBuiltServiceCard, PreBuiltServiceCardSkeleton } from "prebuilt-services/components/PreBuiltServiceCard";
import { PreBuiltServiceViewComponent } from "prebuilt-services/components/PreBuiltServiceViewComponent";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { useGetScreenProperties } from "lib/utils";
import { useInfiniteGridScroll, IntersectionObserverTargetComponent, useGetMaxGridItemsToFit } from "ui-widgets/lists";
import { Grid, GridSize } from "@mui/material";

const gridColumns = { xs: 1, sm: 1, md: 3, lg: 3, xl: 4 };

const PreBuiltServiceGridContainerLayout = React.forwardRef<HTMLDivElement, any>(({ children }, ref) => (
  <Grid container spacing={{ xs: 1, md: 2 }} ref={ref}>
    {children}
  </Grid>
));

const PreBuiltServiceGridItemLayout = ({ children, key }) => {
  return (
    <Grid
      item
      xs={(12 / gridColumns.xs) as GridSize}
      sm={(12 / gridColumns.sm) as GridSize}
      md={(12 / gridColumns.md) as GridSize}
      lg={(12 / gridColumns.lg) as GridSize}
      xl={(12 / gridColumns.xl) as GridSize}
      key={key}
    >
      {children}
    </Grid>
  );
};

interface PreBuiltServicesListProps {
  categoryId: PrimaryKeyValue<ProcessTemplateCategory>;
}

export const PreBuiltServicesList = ({ categoryId }: PreBuiltServicesListProps) => {
  const {
    selectedPreBuilt,
    setSelectedPreBuilt,
    showFallback,
    showLoader,
    prebuilt_services,
    isAPIError,
    fetchPreBuiltServicesStatusCode,
    gridContainerRef,
    setObserverTargetElementRef,
    remainingItemsCount,
  } = usePreBuiltServicesListLogic(categoryId);

  return (
    <>
      {selectedPreBuilt ? (
        <>
          <PreBuiltServiceViewComponent
            handleBackClick={() => setSelectedPreBuilt(null)}
            prebuilt_service={selectedPreBuilt}
          />
        </>
      ) : (
        <>
          <APIListProgressWrapper
            showLoader={false}
            showMessageWhenEmpty={showFallback}
            items={prebuilt_services}
            noDataMessage="Sorry, there are no templates available to install."
            isAPIError={isAPIError}
            httpResponseStatusCode={fetchPreBuiltServicesStatusCode}
          >
            <PreBuiltServiceGridContainerLayout ref={gridContainerRef}>
              {prebuilt_services?.map((prebuilt_service, index) => {
                return (
                  <PreBuiltServiceGridItemLayout key={`${prebuilt_service.title}-${prebuilt_service.id}`}>
                    <IntersectionObserverTargetComponent
                      itemsListLength={prebuilt_services.length}
                      itemIndex={index}
                      elementRef={setObserverTargetElementRef}
                    >
                      <PreBuiltServiceCard
                        onSelectTemplate={() => setSelectedPreBuilt(prebuilt_service)}
                        prebuilt_service={prebuilt_service}
                      />
                    </IntersectionObserverTargetComponent>
                  </PreBuiltServiceGridItemLayout>
                );
              })}
              {showLoader &&
                !showFallback &&
                Array(remainingItemsCount)
                  .fill(0)
                  .map((v, index) => (
                    <PreBuiltServiceGridItemLayout key={`item-${index}`}>
                      <PreBuiltServiceCardSkeleton />
                    </PreBuiltServiceGridItemLayout>
                  ))}
            </PreBuiltServiceGridContainerLayout>
          </APIListProgressWrapper>
        </>
      )}
    </>
  );
};

const usePreBuiltServicesListLogic = (categoryId: PrimaryKeyValue<ProcessTemplateCategory>) => {
  const { screenBreakpoint } = useGetScreenProperties();

  const { gridContainerRef, responsiveItemsToFit } = useGetMaxGridItemsToFit({
    estimatedGridItemHeight: 300,
    gridColumnCount: gridColumns[screenBreakpoint],
  });

  const {
    currentItems: prebuilt_services,
    isAPIError,
    httpResponseStatusCode: fetchPreBuiltServicesStatusCode,
    hasMoreItems,
    remainingItemsCount,
    isSuccessfulAPIRequest,
    lazyLoadData,
  } = useAPILazyLoadPreBuiltServiceTemplates({
    apiQueryParams: categoryId ? { category_id: categoryId } : {},
    dependencyList: [categoryId],
    makeRequestOnInitialMount: false, // false because we only want to make the request once we have determined how many items can fit on the Grid container.
    apiPageLimit: responsiveItemsToFit,
  });

  const { setObserverTargetElementRef } = useInfiniteGridScroll({
    lazyLoadData,
  });

  const [selectedPreBuilt, setSelectedPreBuilt] = React.useState<PreBuiltService>();

  React.useEffect(() => {
    setSelectedPreBuilt(null);
  }, [categoryId]);

  const showLoader = hasMoreItems;

  const showFallback = isSuccessfulAPIRequest && prebuilt_services?.length === 0;

  return {
    selectedPreBuilt,
    setSelectedPreBuilt,
    prebuilt_services,
    isAPIError,
    fetchPreBuiltServicesStatusCode,
    gridContainerRef,
    setObserverTargetElementRef,
    remainingItemsCount,
    showLoader,
    showFallback,
  };
};
